/**
 * /dashboard/offboarding/letters/$id/acknowledge — recipient-only letter
 * acknowledgement page (G4.7).
 *
 * The ONLY recipient-facing surface in the offboarding module — every
 * other route is HR-admin gated. A warning letter must be acknowledged
 * by the recipient (and only the recipient) before HR can rely on the
 * "they were notified" disposition.
 *
 * Backend wire contract:
 *  - GET /offboarding/employees/{employee_id}/letters — the only read
 *    surface that exposes a letter row to an employee. There is NO
 *    single GET /letters/{id} endpoint, so we hydrate the page state
 *    from the list call against the SIGNED-IN user's id.
 *  - GET /offboarding/letters/{id}/download.pdf — streams the PDF with
 *    Content-Disposition: attachment.
 *  - POST /offboarding/letters/{id}/acknowledge — recipient-only,
 *    warning letters only.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/offboarding/letters/[id]/
 * acknowledge/page.tsx. Swaps:
 *   - "use client" dropped
 *   - use(params) → Route.useParams()
 *   - useRouter → useNavigate
 *   - next/link Link → @tanstack/react-router Link
 */

import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import {
  ArrowLeft,
  CheckCircle2,
  FileText,
  Loader2,
  ShieldX,
} from "lucide-react";
import { toast } from "sonner";
import {
  useAcknowledgeLetter,
  useEmployeeLetters,
} from "@/hooks/use-offboarding";
import { useAuth } from "@/lib/auth";
import { offboardingApi, toApiError } from "@/lib/api";
import {
  fmtDateTime,
  letterTypeLabel,
} from "@/lib/offboarding-format";
import type { OffboardingLetter } from "@/types/offboarding";
import { PageHeader } from "@/components/shared/page-header";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/hr/offboarding/letters/$id/acknowledge",
)({
  component: AcknowledgeLetterRoute,
});

/**
 * Renders the right disposition card for the ack flow: already-ack'd
 * confirmation, non-warning info banner, or the ack-button prompt for
 * a fresh warning letter.
 */
function AcknowledgeArea({
  letter,
  onAcknowledge,
  pending,
}: Readonly<{
  letter: OffboardingLetter;
  onAcknowledge: () => Promise<void> | void;
  pending: boolean;
}>) {
  if (letter.acknowledged_at) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50/60 px-3 py-3">
        <CheckCircle2
          aria-hidden
          className="mt-0.5 h-5 w-5 text-green-700"
        />
        <div className="space-y-0.5">
          <p className="text-sm font-medium text-green-900">
            Acknowledged on {fmtDateTime(letter.acknowledged_at)}
          </p>
          <p className="text-xs text-green-900/80">
            Your acknowledgement has been recorded.
          </p>
        </div>
      </div>
    );
  }
  if (letter.letter_type !== "warning") {
    return (
      <Alert>
        <AlertTitle>
          This letter type does not require acknowledgement
        </AlertTitle>
        <AlertDescription>
          Only warning / show-cause notices need to be acknowledged. You
          can keep this preview for your records.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        By clicking the button below you confirm that you have received
        this notice. Your acknowledgement records only that you have
        seen the letter — it is not an admission of any underlying
        conduct.
      </p>
      <Button
        onClick={() => void onAcknowledge()}
        disabled={pending}
        className="gap-1.5"
      >
        {pending ? (
          <Loader2 aria-hidden className="h-4 w-4 animate-spin" />
        ) : (
          <CheckCircle2 aria-hidden className="h-4 w-4" />
        )}
        I acknowledge receipt of this notice
      </Button>
    </div>
  );
}

function PdfPreview({ letterId }: Readonly<{ letterId: number }>) {
  const q = useQuery({
    queryKey: ["offboarding-letter-pdf-blob", letterId],
    queryFn: () => offboardingApi.downloadLetterBlob(letterId),
    staleTime: 60_000,
    enabled: Number.isFinite(letterId) && letterId > 0,
  });

  const blob = q.data;
  const blobUrl = useMemo(
    () => (blob ? URL.createObjectURL(blob) : null),
    [blob],
  );
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (q.isLoading) {
    return <Skeleton className="h-[480px] w-full" />;
  }
  if (q.isError) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Could not load the letter PDF</AlertTitle>
        <AlertDescription>{toApiError(q.error).message}</AlertDescription>
      </Alert>
    );
  }
  if (!blobUrl) {
    return (
      <p className="rounded-md border border-dashed border-input px-3 py-6 text-center text-sm text-muted-foreground">
        No PDF preview available.
      </p>
    );
  }
  return (
    <iframe
      title="Letter PDF preview"
      src={blobUrl}
      className="h-[480px] w-full rounded-md border border-input bg-white sm:h-[600px]"
    />
  );
}

function AcknowledgeLetterRoute() {
  const { id, tenantSlug } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const ackMut = useAcknowledgeLetter();

  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;

  // The only available read path is the per-employee list — fetch the
  // signed-in user's own letters and find this id.
  const lettersQ = useEmployeeLetters(
    user?.user_id ?? 0,
    undefined,
    !!user?.user_id && user.user_id > 0,
  );

  const letter: OffboardingLetter | undefined = lettersQ.data?.items.find(
    (l) => l.id === numId,
  );

  async function onAcknowledge() {
    if (!letter) return;
    try {
      await ackMut.mutateAsync(letter.id);
      toast.success("Acknowledgement recorded.");
    } catch (err) {
      const apiErr = toApiError(err);
      // letter_already_acknowledged: the query invalidation will refetch
      // and the acknowledged_at will arrive on the row — show the inline
      // tone explicitly so users don't think the click was a no-op.
      if (apiErr.error === "letter_already_acknowledged") {
        toast.info(apiErr.message);
        return;
      }
      toast.error(apiErr.message);
    }
  }

  return (
    <>
      <PageHeader
        title="Acknowledge letter"
        description={
          letter
            ? `Review the ${letterTypeLabel(letter.letter_type).toLowerCase()} and confirm receipt.`
            : "Review the letter and confirm receipt."
        }
        actions={
          <Button asChild variant="outline" className="gap-1.5">
            <Link to="/app/$tenantSlug" params={{ tenantSlug }}>
              <ArrowLeft aria-hidden className="h-4 w-4" />
              Back to dashboard
            </Link>
          </Button>
        }
      />

      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid letter id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid letter id.
          </AlertDescription>
        </Alert>
      )}

      {validId && lettersQ.isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-[480px] w-full" />
        </div>
      )}

      {validId && lettersQ.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this letter</AlertTitle>
          <AlertDescription>
            {toApiError(lettersQ.error).message}
          </AlertDescription>
        </Alert>
      )}

      {validId && lettersQ.data && !letter && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <FileText className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                Letter not found
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                We couldn&apos;t find a letter with this id addressed to
                you. Check the link from your notification email or ask
                HR to re-send.
              </p>
            </div>
            <Button
              variant="outline"
              className="gap-1.5"
              onClick={() => navigate({ to: "/app/$tenantSlug", params: { tenantSlug }, })}
            >
              Back to dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/*
        Client-side recipient guard. The backend already checks this on
        every read + the ack POST; if a non-recipient somehow lands here
        the list above would have returned nothing anyway.
      */}
      {validId && letter && user?.user_id !== letter.employee_id && (
        <Card>
          <CardContent className="flex flex-col items-center gap-3 py-14 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
              <ShieldX className="h-6 w-6" />
            </span>
            <div className="space-y-1">
              <p className="text-base font-medium text-gray-900">
                You don&apos;t have access to acknowledge this letter
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                Only the addressed recipient can acknowledge a letter. If
                this looks wrong, please contact your HR admin.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {validId &&
        letter &&
        user?.user_id === letter.employee_id && (
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <CardTitle className="text-base">
                      {letterTypeLabel(letter.letter_type)}
                    </CardTitle>
                    <CardDescription>
                      Generated {fmtDateTime(letter.generated_at)}
                      {letter.sent_to_employee_at && (
                        <>
                          {" · "}Sent{" "}
                          {fmtDateTime(letter.sent_to_employee_at)}
                        </>
                      )}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <PdfPreview letterId={letter.id} />

                <Separator />

                <AcknowledgeArea
                  letter={letter}
                  onAcknowledge={onAcknowledge}
                  pending={ackMut.isPending}
                />
              </CardContent>
            </Card>
          </div>
        )}
    </>
  );
}
